/**
 * What you owe, and how to settle it.
 *
 * Members could previously see a dues ledger only if an admin showed it to them;
 * there was no way to pay. This is the member's side of the ledger.
 */
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { billingApi } from '../api/billing';
import { useAuth } from '../auth/AuthProvider';
import { toast } from '../lib/toast';
import { Badge, Button } from './ui';

const money = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const STATUS_TONE = {
  pending: 'warning',
  paid: 'success',
  waived: 'neutral',
  cancelled: 'neutral',
} as const;

export function MyDues() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();

  const charges = useQuery({
    queryKey: ['my-charges', user?.id],
    queryFn: () => billingApi.listCharges({ personId: user!.id }),
    enabled: !!user,
  });

  const history = useQuery({
    queryKey: ['my-payments', user?.id],
    queryFn: () => billingApi.paymentHistory({ personId: user!.id }),
    enabled: !!user,
  });

  // Stripe bounces the member back here with ?payment=success|cancelled.
  useEffect(() => {
    const result = params.get('payment');
    if (!result) return;
    if (result === 'success') {
      toast.success('Payment received — thank you. Your receipt is on its way.');
      // The webhook may not have landed yet; refetch shortly so the row flips.
      void qc.invalidateQueries({ queryKey: ['my-charges'] });
      setTimeout(() => void qc.invalidateQueries({ queryKey: ['my-charges'] }), 3000);
    } else if (result === 'cancelled') {
      toast.error('Payment cancelled — nothing was charged.');
    }
    params.delete('payment');
    setParams(params, { replace: true });
  }, [params, setParams, qc]);

  const checkout = useMutation({
    mutationFn: (chargeId: string) => billingApi.checkout({ chargeId, provider: 'stripe' }),
    onSuccess: (result) => {
      if (result.checkoutUrl) {
        // Leave the SPA for Stripe's hosted page.
        window.location.assign(result.checkoutUrl);
        return;
      }
      // Stripe isn't configured — say what to do instead of failing silently.
      toast.error(
        result.message ??
          'Online payment is not available yet. Please contact the treasurer.',
      );
    },
  });

  const rows = charges.data?.data ?? [];
  const outstanding = rows
    .filter((c) => c.status === 'pending')
    .reduce((sum, c) => sum + Number(c.amount), 0);

  return (
    <section className="card space-y-4 p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-display text-xl text-cream-50">Membership dues</h2>
        {outstanding > 0 ? (
          <span className="text-sm text-cream-300/70">
            Outstanding <strong className="text-warning">{money(outstanding)}</strong>
          </span>
        ) : null}
      </div>

      {charges.isLoading ? (
        <p className="text-sm text-cream-300/60">Loading…</p>
      ) : !rows.length ? (
        <p className="text-sm text-cream-300/50">Nothing on your account. </p>
      ) : (
        <ul className="divide-y divide-ink-800">
          {rows.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
              <div className="min-w-0">
                <p className="flex items-center gap-2 text-sm text-cream-100">
                  <span className="truncate">{c.label}</span>
                  <Badge tone={STATUS_TONE[c.status as keyof typeof STATUS_TONE] ?? 'neutral'}>
                    {c.status}
                  </Badge>
                </p>
                <p className="mt-0.5 text-xs text-cream-300/50">
                  {money(Number(c.amount))}
                  {c.dueDate ? ` · due ${new Date(c.dueDate + 'T00:00:00').toLocaleDateString()}` : ''}
                </p>
              </div>

              {c.status === 'pending' ? (
                <Button
                  loading={checkout.isPending && checkout.variables === c.id}
                  onClick={() => checkout.mutate(c.id)}
                >
                  Pay {money(Number(c.amount))}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {history.data?.data?.length ? (
        <div className="border-t border-ink-800 pt-4">
          <h3 className="mb-2 text-xs uppercase tracking-wide text-cream-300/45">
            Payment history
          </h3>
          <ul className="space-y-1.5">
            {history.data.data.map((p) => (
              <li key={p.id} className="flex items-center justify-between text-sm">
                <span className="capitalize text-cream-300/60">
                  {p.provider} · {p.status}
                </span>
                <span className="flex items-center gap-3">
                  <span className="text-cream-100">{money(Number(p.amount))}</span>
                  {p.receiptUrl ? (
                    <a
                      href={p.receiptUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-gold-400 hover:text-gold-200"
                    >
                      Receipt
                    </a>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
